'use client'
import {
  useGetCookieConsentQuery,
  useGetUpdateCookieConsentMutation,
} from '@/app/globalRedux/api/constantSlice'
import { RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import {
  ApplyPreferencesOptions,
  CookieConsentResponseData,
  CookieContextType,
  CookiePreferences,
} from '@/app/types/cookieConsentType'
import {
  removeGACookies,
  setBasicCookies,
  setThirdPartyCookie,
  unSetThirdPartyCookie,
} from '@/app/utils/cookiesConsent'
import { getCookies } from '@/lib/utils'
import { useRouter } from 'next/navigation'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'sonner'

const CookieContext = createContext<CookieContextType | undefined>(undefined)

export const CookieProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const user = useSelector((state: RootState) => state.user)
  const isLoggedIn = user?.loggedIn ?? false
  const [updateCookieConsent] = useGetUpdateCookieConsentMutation()
  const { data: cookieConsentData } = useGetCookieConsentQuery<{
    data: CookieConsentResponseData
  }>(undefined, { skip: !isLoggedIn })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showDetailedView, setShowDetailedView] = useState(false)
  const [isNecessarySectionExpanded, setIsNecessarySectionExpanded] =
    useState(true)
  const [isThirdPartySectionExpanded, setIsThirdPartySectionExpanded] =
    useState(false)
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(
    {
      necessary: true,
      thirdParty: false,
    }
  )

  const cookies = getCookies()

  // Note: Be carefull while altering this Function
  //       It may break cookie consent setup
  const applyPreferences = async ({
    action,
    manualTriggerer = false,
    thirPartyValue,
  }: ApplyPreferencesOptions) => {
    try {
      setBasicCookies()
      let thirdPartyEnabled = thirPartyValue ?? cookiePreferences.thirdParty
      if (action === 'all') {
        thirdPartyEnabled = true
      } else if (action === 'decline') {
        thirdPartyEnabled = false
      }

      // For manual Triggers like allow all , decline , allow selectioon
      if (isLoggedIn && thirPartyValue === undefined) {
        const response = await updateCookieConsent({
          thirdParty: thirdPartyEnabled,
        }).unwrap()
        if (response?.status === 'success') {
          toast.success(t('cookie_success'))
          setIsModalOpen(false)

          if (thirdPartyEnabled) {
            setThirdPartyCookie()
          } else if (!thirdPartyEnabled && cookies?.third_party === 'enabled') {
            unSetThirdPartyCookie()
            window.location.reload()
            return
          } else {
            console.info('Cookies are already disabled')
          }
        }
      } else {
        // updated by user consent
        if (thirdPartyEnabled && cookies?.third_party === 'enabled') {
          return
        } else if (!thirdPartyEnabled && cookies?.third_party === 'disabled') {
          removeGACookies()
        } else if (!thirdPartyEnabled && cookies?.third_party === 'enabled') {
          unSetThirdPartyCookie()
          window.location.reload()
          return
        } else if (thirdPartyEnabled && cookies?.third_party === 'disabled') {
          setThirdPartyCookie()
          window.location.reload()
          return
        } else if (cookies?.third_party == undefined && thirdPartyEnabled) {
          setThirdPartyCookie()
        } else if (cookies?.third_party == undefined && !thirdPartyEnabled) {
          unSetThirdPartyCookie()
          window.location.reload()
        }
        manualTriggerer && setIsModalOpen(false)
      }

      setShowDetailedView(false)
      router.refresh()
    } catch (error: any) {
      toast.error(error?.data?.error || t('cookie_error'))
      console.error('Error occurred while updating cookies:', error)
    } finally {
    }
  }

  useEffect(() => {
    if (!cookieConsentData || cookieConsentData.status !== 'success') return

    const expired = cookieConsentData.cookie_consent?.expired
    const thirdPartyPref = cookieConsentData.cookie_consent?.googleAnalytics

    if (expired) {
      setIsModalOpen(true)
    } else {
      setIsModalOpen(false)
    }

    setCookiePreferences({ necessary: true, thirdParty: thirdPartyPref })
    applyPreferences({
      action: 'selected',
      manualTriggerer: false,
      thirPartyValue: thirdPartyPref,
    })
  }, [cookieConsentData])

  // note: ensure to remove GA cookies && open modal if not set anything
  useEffect(() => {
    if (cookies?.basic_cookie !== 'enabled') {
      setIsModalOpen(true)
      return
    }
  }, [cookies])

  useEffect(() => {
    if (!isLoggedIn && cookies?.third_party === 'disabled') {
      removeGACookies()
    }
  }, [])

  return (
    <CookieContext.Provider
      value={{
        isModalOpen,
        showDetailedView,
        cookiePreferences,
        setCookiePreferences,
        isNecessarySectionExpanded,
        isThirdPartySectionExpanded,
        setIsModalOpen,
        setShowDetailedView,
        setIsNecessarySectionExpanded,
        setIsThirdPartySectionExpanded,
        applyPreferences,
      }}
    >
      {children}
    </CookieContext.Provider>
  )
}

// Custom Hook for Context Usage
export const useCookieContext = () => {
  const context = useContext(CookieContext)
  if (!context) {
    throw new Error('useCookieContext must be used within a CookieProvider')
  }
  return context
}
